import React from 'react'
import { connect } from 'react-redux'
import Path from '@leverege/path'
import { TreeOrganizer } from '@leverege/api-attributes'
import { Label } from '@leverege/ui-elements'
import { ListGroup } from 'react-bootstrap'

import ItemFactory from './ItemFactory'
import DetailFactory from './DetailFactory'
import AddDialog from './AddDialog'
import ComponentAttrs from '~/editors/attributes'
import AddRelationshipDialog from './AddRelationshipDialog'
import { TextInput, LabelGroup, Loading } from '~/views'

import { getBlueprint } from '~/actions/BlueprintActions'
import { AttributeUpdater, BlueprintActions, BreadcrumbActions } from '~/actions'
import AttributeTree from '~/reducers/AttributeTree'

import style from './style.css'

class AttributesScreen extends React.Component {
  
  static propTypes = {
  }

  constructor( props ) {
    super( props )
    this.state = { 
      showAddDialog : false, 
      selectedPath : '', 
      addingPath : '',
      relationshipPath : '',
    }
  }

  static getDerivedStateFromProps( nextProps ) {

    if ( nextProps.blueprint == null ) {
      return null
    }

    const bp = nextProps.blueprint.data
    if ( bp == null ) {
      return null
    }

    nextProps.dispatch( BreadcrumbActions.setBreadcrumbs( [ 
      { name : 'Blueprints', link : `/project/${bp.projectId}/blueprint` }, 
      { name : bp.name, link : `/project/${bp.projectId}/blueprint/${bp.id}` },
      { name : 'Attributes' }, 
    ] ) )
    
    return null    
  }
  
  componentDidMount() {
    const { match, dispatch } = this.props
    dispatch( getBlueprint( match.params.projectId, match.params.blueprintId ) )
  }

  /**
   * Performs an attribute tree traversal side effect
   */
  componentDidUpdate( prevProps, prevState ) {
    const { blueprint } = this.props 

    if ( this.selectWhenFound ) {
      // If we are waiting to select a node, try to find it.
      const node = AttributeTree
        .findNodeByPath( blueprint.attributeTree, this.selectWhenFound )
      if ( node ) {
        this.onSelect( node )
        this.selectWhenFound = null
      }
    } 
  }

  onSelect = ( item ) => {
    let selectedPath = ''
    if ( item ) { 
      if ( AttributeTree.isGroup( item ) ) {
        selectedPath = item.path
      } else if ( AttributeTree.isAttribute( item ) ) {
        selectedPath = Path( item.path )
          .removeLast()
          .path()
      }
      if ( selectedPath.length > 0 ) {
        selectedPath += '/'
      }
    }

    this.setState( { selected : item, selectedPath, addingPath : selectedPath } );
  }

  onAttributeChange = ( event ) => {
    const { blueprint = {}, dispatch } = this.props 

    const { data : blueprintData } = blueprint
    if ( blueprintData ) {
      AttributeUpdater( dispatch, blueprintData, event.target )
        .set( event.path, event.value )
        .done()
    }
  }

  onAddNewItem = ( value, err ) => {
    if ( !err && value !== '' ) { 
      this.setState( { showAddDialog : true, addingPath : value } )
    }
  }

  onAddDialogSelection = ( value ) => { 
    const { selectedPath, addingPath } = this.state 
    const { blueprint, dispatch } = this.props 

    let newSelectedPath = selectedPath
    const path = Path( addingPath ) // this.state.selectedPath + this.state.addingPath

    if ( value === AddDialog.GROUP ) {
      dispatch( BlueprintActions.addAttributeGroup( path.path() ) )
      newSelectedPath = path.path() 
      this.selectWhenFound = newSelectedPath 
    } else if ( value.typeName() === 'relationship' ) {
      if ( path.pathArray().length !== 1 ) {
        return null
      }
      this.setState( { showRelationshipDialog : true, relationshipPath : path.path() } )
    } else {
      // value is a Creator
      const attr = value.create( path.path(), path.last() )
      dispatch( BlueprintActions.addAttribute( blueprint.data, attr ) )
      this.selectWhenFound = path.path()
      newSelectedPath = path.removeLast( true ).path()
    }

    if ( newSelectedPath.length > 0 && newSelectedPath[newSelectedPath.length - 1] !== '/' ) {
      newSelectedPath += '/'
    }
    this.setState( { showAddDialog : false, addingPath : newSelectedPath, selectedPath : newSelectedPath } );
  }

  onAddRelationship = ( relationship ) => {
    const { dispatch, blueprint } = this.props 

    dispatch( BlueprintActions.addAttribute( blueprint.data, relationship ) )
    this.selectWhenFound = relationship.path

    this.setState( { 
      showRelationshipDialog : false,
      addingPath : relationship.path, 
      selectedPath : '', 
    } )
  
  }

  render() {
    const { selected, showRelationshipDialog, addingPath, relationshipPath, showAddDialog } = this.state 
    const { blueprint = {}, match } = this.props 

    const { data : blueprintData } = blueprint
    if ( blueprintData == null || blueprintData.id !== match.params?.blueprintId ) {
      return <Loading show message="Loading Blueprint..." />
    }
    const tree = blueprint.attributeTree;

    const nTree = new TreeOrganizer()
    nTree.attributes = blueprintData?.attributes

    const selectedNode = AttributeTree.findNodeById( tree, selected && selected.id )
    const rootItem = ItemFactory.createTreeItem( tree, {
      item : tree,
      key : tree.id,
      blueprint : blueprintData,
      onSelect : this.onSelect,
      selectedItem : selectedNode
    } )

    const validator = ComponentAttrs.createPathValidator( tree, blueprintData?.attributes )

    const expandedValidator = ( value ) => {
      if ( value === '' ) {
        return true
      }
      if ( value?.length > 500 ) {
        return false
      }
      return validator( value )
    }

    const acceptKey = ComponentAttrs.EventKeyPathValidator
    const closeAddDialog = () => this.setState( { showAddDialog : false } )

    const detail = DetailFactory.createDetails( selectedNode, 
      { blueprint : blueprintData,
        attribute : selectedNode, 
        currentTags : blueprint.currentTags,
        onChange : this.onAttributeChange, 
      } )

    return (
      <div className={style.screen}>
        <div className={style.attributeEditor}>
          <div className={style.leftSide}>
            <div className={style.newItemGroup}>
              <div className={style.newItemCTA}>
                Enter the name of the attribute to add to your blueprint. 
                <div className={style.newItemCTAHint}>
                  {'You can use \'/\' to specify groups. ex engine1/battery/amps'}
                </div>
              </div>
              <LabelGroup className={style.newItemEntryPane}>
                <Label className={style.currentSelectedGroup}>Path</Label>
                <TextInput className={style.newItemInput} 
                  hint="Enter name for field or group to add..."
                  changeOnBlur={false}
                  value={addingPath}
                  alwaysFireOnReturn
                  placeCaretAtEnd
                  onChange={this.onAddNewItem}
                  validator={expandedValidator}
                  acceptKey={acceptKey} />
              </LabelGroup>
            </div>

            <ListGroup className={style.tree}>
              <div>
                {rootItem}
              </div>
            </ListGroup>
          </div>
          <div className={style.details}>
            {detail}
          </div>
        </div>

        <AddRelationshipDialog
          key={`${blueprint.id}-${relationshipPath}`}
          show={showRelationshipDialog} 
          onAddRelationship={this.onAddRelationship}
          projectId={match.params?.projectId}
          path={relationshipPath}
          onCancel={() => this.setState( { showRelationshipDialog : false } )}
          srcName={blueprint?.data?.name}
          blueprint={blueprint} />
        <AddDialog show={showAddDialog} 
          onHide={closeAddDialog} 
          onTypeSelected={this.onAddDialogSelection}
          path={addingPath} />
      </div>
    )
  } 
 
}

export default connect(
  state => ( {
    blueprint : state.blueprint,
  } )
)( AttributesScreen )
